import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, DialogActions, DialogContent, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { HandlerAutocompleteField } from "../../../shared/Autocompletes/HandlerAutocompleteField";
import { formatCurrency, formatPercentage } from "../../../utils/namesFormater";
import CurrencyTextField from "../../../shared/UIParts/InputFields/CurrencyTextField";
import PolicyPickList from "./PolicyPickList";
import TargetFeesFields, { hasDepositFee as productHasDepositFee, useProposedSavingFee } from "./TargetFeesFields";
import {
  TransferAction,
  buildNewTransferTarget,
  isBasketNewFund,
  newActionId,
  policyKey,
  policyLabel,
  productLabel,
  sumFunds,
} from "./quickActions";

type TargetMode = "existing" | "new";
type AmountMode = "all" | "part";

interface Props {
  /** the client's policies (after the actions already in the basket) */
  policies: MimshakData[];
  /** opened from a specific policy - it is preselected as a source */
  initialSource?: MimshakData | null;
  onComplete: (action: TransferAction) => void;
  onCancel: () => void;
  /** under each policy list (e.g. "הצג הכל" when the policies are filtered) */
  policiesFooter?: React.ReactNode;
}

const productOf = (p: MimshakData) => p.adjusted_product_type ?? p.product_type;

/** the sources' saving fee, weighted by their balance (null - no source has a fee) */
const weightedSavingFee = (sources: MimshakData[]): number | null => {
  const withFee = sources.filter((s) => s.saving_fee != null);
  const total = sumFunds(withFee);
  if (withFee.length === 0) return null;
  if (total === 0) return withFee[0].saving_fee;
  return withFee.reduce((sum, s) => sum + s.saving_fee! * (s.funds_sum || 0), 0) / total;
};

/**
 * ניוד: מאילו קופות (אחת, או כמה מאותו סוג – איחוד קופות) → כמה (כל הכסף, או חלק – מקופה אחת בלבד) →
 * לאן (קופה קיימת מאותו סוג, או קופה חדשה אצל יצרן) → דמי הניהול ביעד (מוצע לפי הסכמי העמלות של היצרן,
 * ניתן לשינוי). רק בונה את הפעולה ומחזיר אותה ב-onComplete – לא שומר. כך אפשר להטמיע אותו גם במעבדה.
 */
const TransferWizard: React.FC<Props> = ({ policies, initialSource = null, onComplete, onCancel, policiesFooter }) => {
  const [sources, setSources] = useState<MimshakData[]>(initialSource ? [initialSource] : []);
  const [sourcesConfirmed, setSourcesConfirmed] = useState(false);
  const [amountMode, setAmountMode] = useState<AmountMode>("all");
  const [partialAmount, setPartialAmount] = useState<number | null>(null);
  const [targetMode, setTargetMode] = useState<TargetMode>("new");
  const [existingTarget, setExistingTarget] = useState<MimshakData | null>(null);
  const [newHandlerCode, setNewHandlerCode] = useState("");
  const [savingFee, setSavingFee] = useState<number | null>(null);
  const [depositFee, setDepositFee] = useState<number | null>(null);

  // only a fund with money can be transferred, and not a new fund created by this basket
  const sourceOptions = useMemo(() => policies.filter((p) => (p.funds_sum || 0) > 0 && !isBasketNewFund(p)), [policies]);
  const sourceKeys = useMemo(() => new Set(sources.map(policyKey)), [sources]);
  const sourceProduct = sources.length > 0 ? productOf(sources[0]) : null;
  const hasDepositFee = productHasDepositFee(sourceProduct);

  const existingTargets = useMemo(
    () => policies.filter((p) => !sourceKeys.has(policyKey(p)) && productOf(p) === sourceProduct),
    [policies, sourceKeys, sourceProduct]
  );

  const targetHandler = targetMode === "existing" ? existingTarget?.handler_code ?? "" : newHandlerCode;
  // a partial transfer - from one fund only
  const canBePartial = sources.length === 1;
  const isPartial = canBePartial && amountMode === "part";
  const sourcesBalance = sumFunds(sources);
  const partialError =
    !isPartial || partialAmount == null
      ? null
      : partialAmount <= 0
      ? "יש להזין סכום גדול מאפס"
      : partialAmount >= sourcesBalance
      ? `סכום חלקי צריך להיות קטן מהיתרה (${formatCurrency(sourcesBalance)}) – אחרת זה ניוד של כל הכסף`
      : null;
  const transferred = isPartial ? partialAmount ?? 0 : sourcesBalance;
  const currentSavingFee = useMemo(() => weightedSavingFee(sources), [sources]);

  // proposed fee: the handler's agreement for the client's total there after the transfer (as the lab)
  const alreadyAtTarget = useMemo(
    () => (targetHandler ? sumFunds(policies.filter((p) => p.handler_code === targetHandler && !sourceKeys.has(policyKey(p)))) : 0),
    [targetHandler, policies, sourceKeys]
  );
  const proposedSavingFee = useProposedSavingFee(targetHandler, alreadyAtTarget + transferred);

  // a new target - prefill the fees (an existing fund: its own fees, a new one: the proposed fee)
  const targetKey = targetMode === "existing" ? (existingTarget ? policyKey(existingTarget) : "") : `new|${newHandlerCode}`;
  useEffect(() => {
    const saving = targetMode === "existing" ? existingTarget?.saving_fee ?? proposedSavingFee : proposedSavingFee;
    const deposit = targetMode === "existing" ? existingTarget?.deposit_fee : sources[0]?.deposit_fee;
    setSavingFee(saving ?? null);
    setDepositFee(deposit ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, proposedSavingFee]);


  const toggleSource = (policy: MimshakData) =>
    setSources((prev) =>
      prev.some((s) => policyKey(s) === policyKey(policy)) ? prev.filter((s) => policyKey(s) !== policyKey(policy)) : [...prev, policy]
    );

  const amountValid = !isPartial || (partialAmount != null && !partialError);
  const canComplete = sources.length > 0 && amountValid && (targetMode === "existing" ? !!existingTarget : !!newHandlerCode);

  const handleComplete = () => {
    if (!canComplete) return;
    const isNewTarget = targetMode === "new";
    onComplete({
      kind: "transfer",
      id: newActionId(),
      sources,
      isPartial,
      amount: transferred,
      target: isNewTarget ? buildNewTransferTarget(sources, newHandlerCode, isPartial ? transferred : null) : existingTarget!,
      isNewTarget,
      newSavingFee: savingFee,
      newDepositFee: hasDepositFee ? depositFee : null,
    });
  };

  if (!sourcesConfirmed) {
    return (
      <>
        <DialogContent sx={{ p: 0 }}>
          <Typography variant="subtitle2" sx={{ px: 3, pt: 1 }}>
            מאילו קופות לנייד?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ px: 3, display: "block" }}>
            אפשר לבחור כמה קופות מאותו סוג – הן יאוחדו לקופה אחת
          </Typography>
          <PolicyPickList
            policies={sourceOptions}
            onPick={toggleSource}
            isChecked={(p) => sourceKeys.has(policyKey(p))}
            isDisabled={(p) => sourceProduct !== null && productOf(p) !== sourceProduct}
            emptyText="אין קופות עם יתרה לניוד"
            footer={policiesFooter}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {sources.length > 0 && (
            <Typography variant="body2" sx={{ ml: "auto" }}>
              {sources.length} קופות · {formatCurrency(transferred)}
            </Typography>
          )}
          <Button onClick={onCancel} variant="outlined" size="large">
            חזור
          </Button>
          <Button onClick={() => setSourcesConfirmed(true)} disabled={sources.length === 0} variant="contained" size="large">
            המשך
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {sources.length === 1 ? "מקופה" : `איחוד ${sources.length} קופות ${productLabel(sources[0])}`}
            </Typography>
            {sources.map((s) => (
              <Typography key={policyKey(s)} variant="body1" fontWeight={600}>
                {policyLabel(s)} · {s.policy_number} · {formatCurrency(s.funds_sum || 0)}
                {s.saving_fee != null && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}
                    · ד"נ צבירה {formatPercentage(s.saving_fee)}
                  </Typography>
                )}
              </Typography>
            ))}
            {sources.length > 1 && (
              <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                סה"כ {formatCurrency(transferred)}
              </Typography>
            )}
            <Button
              size="small"
              onClick={() => {
                setSourcesConfirmed(false);
                setExistingTarget(null); // it may become a source
              }}
              sx={{ px: 0 }}
            >
              שנה קופות
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              כמה?
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={isPartial ? "part" : "all"} onChange={(_e, v) => v && setAmountMode(v)}>
              <ToggleButton value="all">את כל הכסף</ToggleButton>
              <ToggleButton value="part" disabled={!canBePartial}>
                רק חלק
              </ToggleButton>
            </ToggleButtonGroup>
            {!canBePartial && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                ניוד חלקי – מקופה אחת בלבד
              </Typography>
            )}
            {isPartial && (
              <CurrencyTextField
                label="סכום לניוד"
                value={partialAmount}
                onChange={setPartialAmount}
                size="medium"
                sx={{ mt: 1.5, maxWidth: 320 }}
                maxWidth="320px"
                error={!!partialError}
                helperText={partialError ?? `מתוך ${formatCurrency(sourcesBalance)} – הקופה נשארת פתוחה`}
              />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              לאן?
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={targetMode} onChange={(_e, v) => v && setTargetMode(v)}>
              <ToggleButton value="new">קופה חדשה</ToggleButton>
              <ToggleButton value="existing" disabled={existingTargets.length === 0}>
                קופה קיימת של הלקוח
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {targetMode === "new" ? (
            <Box sx={{ maxWidth: 320 }}>
              <HandlerAutocompleteField value={newHandlerCode} onChange={setNewHandlerCode} placeholder="בחר יצרן מקבל..." />
            </Box>
          ) : existingTarget ? (
            // chosen - only the chosen fund, with an option to change it
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {policyLabel(existingTarget)} · {existingTarget.policy_number} · {formatCurrency(existingTarget.funds_sum || 0)}
              </Typography>
              <Button size="small" onClick={() => setExistingTarget(null)} sx={{ px: 0 }}>
                החלף קופה
              </Button>
            </Box>
          ) : (
            <PolicyPickList policies={existingTargets} onPick={setExistingTarget} footer={policiesFooter} />
          )}

          {targetHandler && (
            <TargetFeesFields
              productType={sourceProduct}
              proposedSavingFee={proposedSavingFee}
              savingFee={savingFee}
              depositFee={depositFee}
              onSavingFeeChange={setSavingFee}
              onDepositFeeChange={setDepositFee}
              currentSavingFee={currentSavingFee}
              amountForDiff={transferred}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" size="large">
          חזור
        </Button>
        <Button onClick={handleComplete} disabled={!canComplete} variant="contained" size="large">
          הוסף ניוד
        </Button>
      </DialogActions>
    </>
  );
};

export default TransferWizard;
